import { Component, Inject, NgZone, OnInit, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { AuthService } from '@/shared/services';
import { CourseInfoWithGradeRanges } from 'src/server/types';
import * as am5 from '@amcharts/amcharts5';
import { FormControl, FormGroup } from '@angular/forms';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { studentList } from '../data';
import { isPlatformBrowser } from '@angular/common';
import * as _ from 'lodash-es';
import { MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'app-pamlab-landing-page',
  templateUrl: './pamlab-landing-page.component.html',
  styleUrls: ['./pamlab-landing-page.component.css'],
  standalone: false,
})
export class PamlabLandingPageComponent implements OnInit {
  // card labels for toggle functionality
  successMetricsCards = [
    'Student Profile',
    'Cohort Profile',
    'Course Recommender',
    'Course Grade Estimation',
  ];
  currentCardSuccess = 0;

  optimizeCourseOfferingsCards = [
    'Program Bottleneck',
    'Course Combinations',
    'Prerequisite Impact',
  ];
  currentCardOptimize = 0;

  adminSupportCards = [
    'APR/Graduation/Summary',
    'Entry or Exit Flow Graph',
    'FTIC Course DWF & APR',
    'Transfer Enrollment & Trends',
  ];
  currentCardAdmin = 0;

  makeDataDrivenDecisionsCards = [
    'APR/Graduation/Summary',
    'Critical Course Score',
    'Entry or Exit Flow Graph',
  ];
  currentCardMake = 0;

  public env = environment;
  public isStudent = this.authService.getRole() === 'student' ? true : false;
  public role = this.authService.getRole();
  public selectedPrereq!: string | undefined;
  public selectedEntryExit: string = 'College';
  public selectedCourseCombo: string = '1 Course';
  public targetCourseData = {
    has_prereqs: true,
    target: {
      course_code: 'ACG3101',
      course_name: 'Intermediate Financial Accounting I',
      course_desc:
        'Accounting principles and financial accounting practices. Particular emphasis on analysis of balance sheet accounts through problem solving.',
      course_college: 'College of Business',
      course_department: ' Department of Accounting & Finance',
      course_hrs: '3',
      course_prereqs: 'ACG2071 AND CGS2570',
    },
    prereqs: [
      {
        course_code: 'ACG2071',
        course_name: 'Principles of Managerial Accounting',
        course_desc:
          'Role of accounting as a tool in decision making process within economic framework of the firm.',
        a_range: {
          sum: 187,
          fail_rate: 13.9,
          pass_rate: 86.1,
          num_failed: 26,
          num_passed: 161,
        },
        b_range: {
          sum: 192,
          fail_rate: 32.81,
          pass_rate: 67.19,
          num_failed: 63,
          num_passed: 129,
        },
        c_range: {
          sum: 124,
          fail_rate: 52.42,
          pass_rate: 47.58,
          num_failed: 65,
          num_passed: 59,
        },
      },
      {
        course_code: 'CGS2570',
        course_name: 'Personal Computer Applications',
        course_desc:
          'An online course that provides practical experience with current and popular office software tools and exposure to the fundamentals of computing and networks. Issues in privacy, ethics, personal conduct, and security in professional settings are also covered. Students learn to use word processing, spreadsheet, presentation, and database software.',
        a_range: {
          sum: 268,
          fail_rate: 27.24,
          pass_rate: 72.76,
          num_failed: 73,
          num_passed: 195,
        },
        b_range: {
          sum: 89,
          fail_rate: 37.08,
          pass_rate: 62.92,
          num_failed: 33,
          num_passed: 56,
        },
        c_range: {
          sum: 50,
          fail_rate: 62,
          pass_rate: 38,
          num_failed: 31,
          num_passed: 19,
        },
      },
    ],
  };
  public selectedPrereqCourse: CourseInfoWithGradeRanges =
    this.targetCourseData.prereqs[0];

  public entryExitMap: { [entryExit: string]: string } = {
    College:
      '../../../../assets/uwf_assets/marketing-page-pics/entry-exit-college.png',
    Department:
      '../../../../assets/uwf_assets/marketing-page-pics/entry-exit-department.png',
    Program:
      '../../../../assets/uwf_assets/marketing-page-pics/entry-exit-program.png',
  };
  public customChartAttributes = [
    {
      name: 'COHORT',
      columnProp: 'cohort',
    },
    {
      name: 'ADMIT TYPE',
      columnProp: 'admit_type',
    },
    {
      name: 'COLLEGE',
      columnProp: 'college',
    },
    {
      name: 'DROPOUT RISK',
      columnProp: 'dropout_risk',
    },
  ];
  public root!: am5.Root;
  private chart!: am5xy.XYChart;
  public customChartForm = new FormGroup({
    xaxis: new FormControl('cohort'),
    attribute: new FormControl('admit_type'),
    group: new FormControl('college'),
    filteredXaxisAttrs: new FormControl<any[]>([]),
    filteredAttrAttrs: new FormControl<any[]>([]),
    filteredGroupAttrs: new FormControl<any[]>([]),
  });

  constructor(
    public authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
  ) {
    this.isStudent = this.role === 'student';
  }

  ngOnInit(): void {
    const filteredCustomChartAttrs = this.customChartAttributes.filter(
      (attr) => attr.columnProp !== this.customChartForm.value.xaxis,
    );
    this.customChartForm.patchValue({
      filteredXaxisAttrs: [...this.customChartAttributes],
      filteredAttrAttrs: [...filteredCustomChartAttrs],
      filteredGroupAttrs: [...filteredCustomChartAttrs],
    });
    this.generateCustomChart();
  }

  showChart(prereqCourseCode: string) {
    this.selectedPrereqCourse = this.targetCourseData.prereqs.find(
      (c) => c.course_code === prereqCourseCode,
    )!;
  }

  selectSuccessMetricsCard(index: number) {
    this.currentCardSuccess = index;
  }

  selectOptimizeCourseOfferingsCard(index: number) {
    this.currentCardOptimize = index;
  }

  selectAdminSupportCard(index: number) {
    this.currentCardAdmin = index;
  }

  selectMakeDataDrivenDecisionsCard(index: number) {
    this.currentCardMake = index;
  }

  generateCustomChart() {
    const { xaxis, attribute, group } = this.customChartForm.value;
    const groupedByXAxis = _.groupBy(studentList, xaxis!);
    const hasAll = !!(xaxis && attribute && group);

    setTimeout(() => {
      this.browserOnly(() => {
        if (!this.root) this.root = am5.Root.new('landing-page-custom-chart');

        // Remove amcharts logo below graphs
        this.root._logo!.dispose();

        // Set themes
        // https://www.amcharts.com/docs/v5/concepts/themes/
        this.root.setThemes([am5themes_Animated.new(this.root)]);

        // if chart is not empty then erase it for new data coming
        if (this.chart) {
          this.chart.series.pop();
          this.root.container.children.pop();
        }

        // Create chart
        // https://www.amcharts.com/docs/v5/charts/xy-chart/
        this.chart = this.root.container.children.push(
          am5xy.XYChart.new(this.root, {
            panX: false,
            panY: false,
            wheelX: 'none',
            wheelY: 'none',
          }),
        );

        // Add cursor
        // https://www.amcharts.com/docs/v5/charts/xy-chart/cursor/
        const cursor = this.chart.set(
          'cursor',
          am5xy.XYCursor.new(this.root, {}),
        );
        cursor.lineY.set('visible', false);

        // Add scrollbar
        // https://www.amcharts.com/docs/v5/charts/xy-chart/scrollbars/

        // Scrollbar X
        var scrollbarX = am5.Scrollbar.new(this.root, {
          orientation: 'horizontal',
        });

        this.chart.set('scrollbarX', scrollbarX);
        this.chart.bottomAxesContainer.children.push(scrollbarX);

        // Create axes
        // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
        const xRenderer = am5xy.AxisRendererX.new(this.root, {
          minGridDistance: 30,
        });
        xRenderer.labels.template.setAll({
          text: '{realName}',
          rotation: hasAll ? -75 : 0,
        });

        const xAxis = this.chart.xAxes.push(
          am5xy.CategoryAxis.new(this.root, {
            maxDeviation: 0,
            categoryField: 'category',
            renderer: xRenderer,
          }),
        );

        const yAxis = this.chart.yAxes.push(
          am5xy.ValueAxis.new(this.root, {
            maxDeviation: 0.3,
            renderer: am5xy.AxisRendererY.new(this.root, {}),
            min: 0,
          }),
        );

        // Create series
        // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
        const series = this.chart.series.push(
          am5xy.ColumnSeries.new(this.root, {
            name: 'Series 1',
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'value',
            sequencedInterpolation: true,
            categoryXField: 'category',
            tooltip: am5.Tooltip.new(this.root, {
              labelText: '{valueY}',
            }),
          }),
        );

        series.columns.template.setAll({
          fillOpacity: 0.9,
          strokeOpacity: 0,
        });
        series.columns.template.adapters.add('fill', (fill, target: any) => {
          return this.chart
            .get('colors')!
            .getIndex(series.columns.indexOf(target));
        });

        series.columns.template.adapters.add(
          'stroke',
          (stroke, target: any) => {
            return this.chart
              .get('colors')!
              .getIndex(series.columns.indexOf(target));
          },
        );

        const customChartData: any[] = [];
        let startingPoint = 0; // Starting range for a label at bottom
        let endingPoint = 0; // Ending range for a label at bottom

        // process data ant prepare it for the chart
        Object.entries(groupedByXAxis)
          .sort((a, b) => a[0].localeCompare(b[0]))
          .forEach(([xaxisKey, students]) => {
            const groupedByAttr = _.groupBy(students, attribute!);

            Object.entries(groupedByAttr)
              .sort((a, b) => a[0].localeCompare(b[0]))
              .forEach(([attrKey, students]) => {
                const tempArray: any[] = [];

                // add items
                const groupedByGroup = _.groupBy(students, group!);
                Object.entries(groupedByGroup)
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .forEach(([groupKey, students]) => {
                    tempArray.push({
                      category: xaxisKey + '_' + attrKey + '_' + groupKey,
                      realName: groupKey,
                      value: students.length,
                      attrKey,
                    });

                    ++endingPoint;
                  });

                // push to the final data
                am5.array.each(tempArray, function (item) {
                  customChartData.push(item);
                });

                // create range for attribute label at the bottom
                const range = xAxis.makeDataItem({});
                xAxis.createAxisRange(range);

                range.set('category', tempArray[0].category);
                range.set(
                  'endCategory',
                  tempArray[tempArray.length - 1].category,
                );

                const label = range.get('label');

                label!.setAll({
                  text: tempArray[0].attrKey,
                  dy: 50,
                  fontWeight: 'bold',
                  // tooltipText: tempArray[0].attrKey,
                });

                const tick = range.get('tick');
                tick!.setAll({
                  visible: true,
                  strokeOpacity: 1,
                  length: 70,
                  location: 0,
                });

                const grid = range.get('grid');
                grid!.setAll({ strokeOpacity: 1 });
              });

            // Create range for xaxis label at the bottom
            const range = xAxis.makeDataItem({});
            xAxis.createAxisRange(range);

            range.set('category', customChartData[startingPoint].category);
            range.set(
              'endCategory',
              customChartData[customChartData.length - 1].category,
            );

            const label = range.get('label');

            label!.setAll({
              text: xaxisKey,
              dy: 100,
              fontWeight: 'bold',
              // tooltipText: xaxisKey,
            });

            const tick = range.get('tick');
            tick!.setAll({
              visible: true,
              strokeOpacity: 1,
              length: 120,
              location: 0,
            });

            const grid = range.get('grid');
            grid!.setAll({ strokeOpacity: 1 });

            startingPoint = endingPoint;
          });

        // add range for the last grid
        const range = xAxis.makeDataItem({});
        xAxis.createAxisRange(range);
        range.set(
          'category',
          customChartData[customChartData.length - 1].category,
        );
        const tick = range.get('tick');
        tick!.setAll({
          visible: true,
          strokeOpacity: 1,
          length: 90,
          location: 1,
        });

        const grid = range.get('grid');
        grid!.setAll({ strokeOpacity: 1, location: 1 });

        xAxis.data.setAll(customChartData);
        series.data.setAll(customChartData);

        // Make stuff animate on load
        // https://www.amcharts.com/docs/v5/concepts/animations/
        series.appear(1000);
        this.chart.appear(1000, 100);
      });
    });
  }

  handleCustomChartXaxisUpdate(event: MatSelectChange) {
    this.generateCustomChart();
    this.customChartForm.patchValue({
      filteredAttrAttrs: this.customChartAttributes.filter(
        (attr) =>
          attr.columnProp !== event.value &&
          attr.columnProp !== this.customChartForm.value.group,
      ),
      filteredGroupAttrs: this.customChartAttributes.filter(
        (attr) =>
          attr.columnProp !== event.value &&
          attr.columnProp !== this.customChartForm.value.attribute,
      ),
    });
  }

  handleCustomChartAttributeUpdate(event: MatSelectChange) {
    this.generateCustomChart();
    this.customChartForm.patchValue({
      filteredXaxisAttrs: this.customChartAttributes.filter(
        (attr) =>
          attr.columnProp !== event.value &&
          attr.columnProp !== this.customChartForm.value.group,
      ),
      filteredGroupAttrs: this.customChartAttributes.filter(
        (attr) =>
          attr.columnProp !== event.value &&
          attr.columnProp !== this.customChartForm.value.xaxis,
      ),
    });
  }

  handleCustomChartGroupUpdate(event: MatSelectChange) {
    this.generateCustomChart();
    this.customChartForm.patchValue({
      filteredXaxisAttrs: this.customChartAttributes.filter(
        (attr) =>
          attr.columnProp !== event.value &&
          attr.columnProp !== this.customChartForm.value.attribute,
      ),
      filteredAttrAttrs: this.customChartAttributes.filter(
        (attr) =>
          attr.columnProp !== event.value &&
          attr.columnProp !== this.customChartForm.value.xaxis,
      ),
    });
  }

  private browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }
}
